/**
 * Save migrations (tech-design §5). Ordered, pure `(v → v+1)` functions run on the
 * *serialized* (plain-object) form of a save, always upgrading an old save to the
 * current format on load. A save is **never dropped** on a version mismatch — an
 * older save migrates forward; a save from an unknown *future* format is handled by
 * the caller ({@link decodeSave}) falling back rather than throwing here.
 *
 * Permanent policy (CLAUDE.md / acceptance): every migration ships with a fixture
 * save and a unit test, so the upgrade path stays covered as the format evolves.
 */
import { SAVE_FORMAT_VERSION } from '@core/index.js';
import type { SerializedGameState } from './codec.js';

/** The format version this build writes and migrates *up to*. */
export const CURRENT_FORMAT_VERSION = SAVE_FORMAT_VERSION;

/**
 * Format v1: the pre-lifetime save. Resources were bare amount strings; the
 * `{ amount, lifetime }` split (needed once prestige gain began reading lifetime
 * totals) landed in v2. Everything else matches the current serialized shape.
 */
export interface SerializedGameStateV1 extends Omit<SerializedGameState, 'resources'> {
  readonly resources: Record<string, string>;
}

/** v1 → v2: wrap each amount-only resource as `{ amount, lifetime: amount }`. */
function v1ToV2(data: unknown): SerializedGameState {
  const v1 = data as SerializedGameStateV1;
  const resources: Record<string, { amount: string; lifetime: string }> = {};
  for (const [id, amount] of Object.entries(v1.resources)) {
    resources[id] = { amount, lifetime: amount };
  }
  return { ...v1, resources, meta: { ...v1.meta, version: 2 } };
}

/**
 * Format v2: the pre-GAM-11 save. `meta` had no `maxAgeOrderReached` (the lifetime
 * stat that gates full-reset prestige layers).
 */
export interface SerializedGameStateV2 extends Omit<SerializedGameState, 'meta'> {
  readonly meta: Omit<SerializedGameState['meta'], 'maxAgeOrderReached'>;
}

/** v2 → v3: default the new lifetime stat to 0 (settle re-derives it next command). */
function v2ToV3(data: unknown): SerializedGameState {
  const v2 = data as SerializedGameStateV2;
  return { ...v2, meta: { ...v2.meta, maxAgeOrderReached: 0, version: 3 } };
}

/** Registry keyed by the version each function migrates *from*. */
const MIGRATIONS: Readonly<Record<number, (data: unknown) => unknown>> = {
  1: v1ToV2,
  2: v2ToV3,
};

/**
 * Migrate a serialized save from `fromVersion` up to {@link CURRENT_FORMAT_VERSION}.
 * Throws only if an intermediate step is missing (a genuine gap in the ladder), which
 * the caller catches and treats as an unrecoverable blob.
 */
export function migrate(data: unknown, fromVersion: number): SerializedGameState {
  let current: unknown = data;
  let version = fromVersion;
  while (version < CURRENT_FORMAT_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) {
      throw new Error(`No migration registered from save format v${String(version)}`);
    }
    current = step(current);
    version += 1;
  }
  return current as SerializedGameState;
}
